import { useId } from "react";

import { Box, Checkbox, Flex, Text } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";

export interface RiskControlOptionPresentationProps {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
}

/**
 * One risk-control checkbox with the effect spelled out beside it, so the
 * consequence is readable without opening the docs.
 *
 * The whole row is the click target: the `label` wraps the control, and the
 * explicit `aria-labelledby` keeps the accessible name to the title rather
 * than letting it swallow the description as well.
 */
export const RiskControlOptionPresentation = ({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: RiskControlOptionPresentationProps) => {
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;

  return (
    <Text
      as="label"
      size="2"
      className={cn(
        "select-none",
        disabled ? "cursor-default" : "cursor-pointer",
      )}
    >
      <Flex gap="3" align="start">
        <Box pt="1">
          <Checkbox
            checked={checked}
            disabled={disabled}
            aria-labelledby={labelId}
            aria-describedby={descriptionId}
            onCheckedChange={(next) => onCheckedChange(next === true)}
          />
        </Box>
        <Flex direction="column" gap="1">
          <Text id={labelId} size="2" weight="medium">
            {label}
          </Text>
          <Text id={descriptionId} size="1" color="gray">
            {description}
          </Text>
        </Flex>
      </Flex>
    </Text>
  );
};
